import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, MapPin, Building, Award, Globe, Camera,
  Save, LogOut, Eye, Plus, X, CheckCircle, AlertCircle, Loader2,
  Facebook, Instagram, Linkedin, FileText, Star
} from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { US_STATES } from '../data/realtors';

const SPECIALTIES_OPTIONS = [
  'First-time Buyers',
  'Investment Properties',
  'Luxury Homes',
  'Condos',
  'New Construction',
  'Vacation Homes',
  'Waterfront Properties',
  'Commercial',
  'Relocation Services',
  'Foreclosures',
  'Short Sales',
  'Multi-family',
  'Land',
  'Senior Living'
];

const LANGUAGES_OPTIONS = [
  'English',
  'Spanish',
  'Vietnamese',
  'Mandarin',
  'Cantonese',
  'Korean',
  'Japanese',
  'French',
  'German',
  'Tagalog',
  'Hindi'
];

const RealtorDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, updateProfile, uploadPhoto, logout, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    licenseNumber: '',
    yearsExperience: '',
    transactionsCompleted: '',
    languages: [],
    specialties: [],
    about: '',
    certifications: [],
    socialMedia: {
      facebook: '',
      instagram: '',
      linkedin: ''
    }
  });

  const [newCertification, setNewCertification] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/realtor/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Load user data into form
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        title: user.title || '',
        email: user.email || '',
        phone: user.phone || '',
        state: user.state || '',
        city: user.city || '',
        licenseNumber: user.licenseNumber || '',
        yearsExperience: user.yearsExperience || '',
        transactionsCompleted: user.transactionsCompleted || '',
        languages: user.languages || [],
        specialties: user.specialties || [],
        about: user.about || '',
        certifications: user.certifications || [],
        socialMedia: user.socialMedia || { facebook: '', instagram: '', linkedin: '' }
      });
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: { ...prev.socialMedia, [platform]: value }
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert)
    }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setLoading(true);
    setError('');
    try {
      await uploadPhoto(file);
      setSuccess('Photo updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setPhotoPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');

    // Client-side validation for required fields
    if (!formData.name || formData.name.trim() === '') {
      setError('Full Name is required');
      return;
    }
    if (!formData.phone || formData.phone.trim() === '') {
      setError('Phone Number is required');
      return;
    }
    if (!formData.state) {
      setError('State is required');
      return;
    }
    if (!formData.city || formData.city.trim() === '') {
      setError('City is required');
      return;
    }

    setLoading(true);

    try {
      await updateProfile({
        name: formData.name.trim(),
        title: formData.title?.trim() || '',
        phone: formData.phone.trim(),
        state: formData.state,
        city: formData.city.trim(),
        license_number: formData.licenseNumber?.trim() || '',
        years_experience: parseInt(formData.yearsExperience) || 0,
        transactions_completed: parseInt(formData.transactionsCompleted) || 0,
        languages: formData.languages || [],
        specialties: formData.specialties || [],
        about: formData.about?.trim() || '',
        certifications: formData.certifications || [],
        social_media: formData.socialMedia || {}
      });
      setSuccess('Profile saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0f1c] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white">
      <style>{`
        .dashboard-card {
          background: linear-gradient(135deg, rgba(20, 27, 45, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .input-field {
          background: rgba(20, 27, 45, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .input-field:focus {
          border-color: rgba(59, 130, 246, 0.5);
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          transition: all 0.3s ease;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(59, 130, 246, 0.3);
        }

        .tab-btn {
          transition: all 0.2s ease;
        }

        .tab-btn.active {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.4);
          color: #3b82f6;
        }

        .chip {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .chip:hover {
          border-color: rgba(59, 130, 246, 0.3);
        }

        .chip.selected {
          background: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.5);
          color: #3b82f6;
        }

        .profile-image-container {
          position: relative;
        }

        .profile-image-container:hover .upload-overlay {
          opacity: 1;
        }

        .upload-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
          cursor: pointer;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            {user.slug && (
              <Link
                to={`/realtor/${user.slug}`}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">View Profile</span>
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Realtor Profile Dashboard</h1>
            <p className="text-gray-400">Update your information to attract potential clients</p>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <p className="text-blue-400">{success}</p>
            </div>
          )}

          {/* Profile Status Banner */}
          {user.status === 'pending' && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <p className="text-yellow-400">
                <strong>Profile pending approval.</strong> Please complete your information to be listed in the Realtor directory.
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`tab-btn px-4 py-2 rounded-lg border border-transparent whitespace-nowrap ${activeTab === 'profile' ? 'active' : 'text-gray-400 hover:text-white'}`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Basic Info
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`tab-btn px-4 py-2 rounded-lg border border-transparent whitespace-nowrap ${activeTab === 'about' ? 'active' : 'text-gray-400 hover:text-white'}`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              About Me
            </button>
            <button
              onClick={() => setActiveTab('expertise')}
              className={`tab-btn px-4 py-2 rounded-lg border border-transparent whitespace-nowrap ${activeTab === 'expertise' ? 'active' : 'text-gray-400 hover:text-white'}`}
            >
              <Award className="w-4 h-4 inline mr-2" />
              Expertise
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`tab-btn px-4 py-2 rounded-lg border border-transparent whitespace-nowrap ${activeTab === 'social' ? 'active' : 'text-gray-400 hover:text-white'}`}
            >
              <Globe className="w-4 h-4 inline mr-2" />
              Social Media
            </button>
          </div>

          {/* Tab Content */}
          <div className="dashboard-card rounded-2xl p-6 md:p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Photo Upload */}
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="profile-image-container w-32 h-32">
                    <img
                      src={photoPreview || user.photo || 'https://via.placeholder.com/128?text=Photo'}
                      alt={user.name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-blue-500/30"
                    />
                    <div className="upload-overlay" onClick={() => fileInputRef.current?.click()}>
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                    <p className="text-gray-400">{user.email}</p>
                    {user.rating > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-white">{user.rating}</span>
                        <span className="text-gray-500">({user.reviewCount} reviews)</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Title</label>
                    <div className="relative">
                      <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                        placeholder="REALTOR® / Team Leader"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">State *</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <select
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white appearance-none"
                      >
                        <option value="">Select state...</option>
                        {US_STATES.map(state => (
                          <option key={state.code} value={state.code}>{state.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">City *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="input-field w-full px-4 py-3 rounded-xl text-white"
                      placeholder="Houston"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">License Number</label>
                    <input
                      type="text"
                      value={formData.licenseNumber}
                      onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                      className="input-field w-full px-4 py-3 rounded-xl text-white"
                      placeholder="TX123456"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Years of Experience</label>
                    <input
                      type="number"
                      value={formData.yearsExperience}
                      onChange={(e) => handleInputChange('yearsExperience', e.target.value)}
                      className="input-field w-full px-4 py-3 rounded-xl text-white"
                      placeholder="5"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Transactions Completed</label>
                    <input
                      type="number"
                      value={formData.transactionsCompleted}
                      onChange={(e) => handleInputChange('transactionsCompleted', e.target.value)}
                      className="input-field w-full px-4 py-3 rounded-xl text-white"
                      placeholder="100"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* About Tab */}
            {activeTab === 'about' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">About Me *</label>
                  <textarea
                    value={formData.about}
                    onChange={(e) => handleInputChange('about', e.target.value)}
                    className="input-field w-full px-4 py-3 rounded-xl text-white h-64 resize-none"
                    placeholder="Tell your story... What makes you unique? Why should clients choose you?"
                  />
                  <p className="text-gray-500 text-sm mt-2">This is the most important section to attract clients. Write authentically and professionally.</p>
                </div>
              </div>
            )}

            {/* Expertise Tab */}
            {activeTab === 'expertise' && (
              <div className="space-y-8">
                {/* Languages */}
                <div>
                  <label className="block text-white font-medium mb-4">Languages</label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES_OPTIONS.map(lang => (
                      <button
                        key={lang}
                        onClick={() => toggleArrayItem('languages', lang)}
                        className={`chip px-4 py-2 rounded-lg text-sm ${formData.languages.includes(lang) ? 'selected' : 'text-gray-400'}`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Specialties */}
                <div>
                  <label className="block text-white font-medium mb-4">Specialties</label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES_OPTIONS.map(spec => (
                      <button
                        key={spec}
                        onClick={() => toggleArrayItem('specialties', spec)}
                        className={`chip px-4 py-2 rounded-lg text-sm ${formData.specialties.includes(spec) ? 'selected' : 'text-gray-400'}`}
                      >
                        {spec}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div>
                  <label className="block text-white font-medium mb-4">Certifications</label>
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                      className="input-field flex-1 px-4 py-2 rounded-xl text-white"
                      placeholder="Enter certification name..."
                    />
                    <button
                      onClick={addCertification}
                      className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-xl text-blue-400 hover:bg-blue-500/30 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.certifications.map(cert => (
                      <span key={cert} className="chip selected px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        {cert}
                        <button onClick={() => removeCertification(cert)} className="hover:text-red-400">
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Social Tab */}
            {activeTab === 'social' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Facebook</label>
                  <div className="relative">
                    <Facebook className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                    <input
                      type="url"
                      value={formData.socialMedia.facebook}
                      onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="https://facebook.com/yourprofile"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Instagram</label>
                  <div className="relative">
                    <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400" />
                    <input
                      type="url"
                      value={formData.socialMedia.instagram}
                      onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="https://instagram.com/yourprofile"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">LinkedIn</label>
                  <div className="relative">
                    <Linkedin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                    <input
                      type="url"
                      value={formData.socialMedia.linkedin}
                      onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-800 flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="btn-primary px-8 py-3 rounded-xl text-white font-semibold flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorDashboard;
